import {
  RecordContextProvider,
  ResourceContextProvider,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { ReferenceField } from "@/components/admin/reference-field";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { RelativeDate } from "../misc/RelativeDate";
import { Note } from "../notes/Note";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ActivityCompanyNoteCreated, Company } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";
import { ActivityLogNote } from "./ActivityLogNote";

type ActivityLogCompanyNoteCreatedProps = {
  activity: ActivityCompanyNoteCreated;
};

/**
 * A note written on a company. On the company page it can be edited and
 * deleted in place; elsewhere it links to the company.
 */
export function ActivityLogCompanyNoteCreated({
  activity,
}: ActivityLogCompanyNoteCreatedProps) {
  const context = useActivityLogContext();
  if (context === "company") {
    return <EditableCompanyNote activity={activity} />;
  }
  return <CompanyNoteSummary activity={activity} />;
}

const EditableCompanyNote = ({
  activity,
}: ActivityLogCompanyNoteCreatedProps) => {
  const { refetch } = useListContext();
  return (
    <ResourceContextProvider value="company_notes">
      {/* The note header reads company_id from the record context for the avatar. */}
      <RecordContextProvider value={activity.companyNote}>
        <Note note={activity.companyNote} onChange={refetch} />
      </RecordContextProvider>
    </ResourceContextProvider>
  );
};

const CompanyNoteSummary = ({
  activity,
}: ActivityLogCompanyNoteCreatedProps) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { companyNote } = activity;
  const isCurrentUser = activity.sales_id === identity?.id;
  const salesName = useGetSalesName(activity.sales_id, {
    enabled: !isCurrentUser,
  });
  return (
    <ActivityLogNote
      header={
        <div className="flex items-start gap-2 w-full">
          <ReferenceField
            source="company_id"
            reference="companies"
            record={companyNote}
            link={false}
            render={({ referenceRecord }) =>
              referenceRecord ? (
                <>
                  <CompanyAvatar
                    width={20}
                    height={20}
                    record={referenceRecord as Company}
                  />
                  <span className="text-muted-foreground text-sm flex-grow">
                    {translate(
                      isCurrentUser
                        ? "crm.activity.you_added_note"
                        : "crm.activity.added_note",
                      { name: salesName },
                    )}{" "}
                    <Link to={`/companies/${referenceRecord.id}/show`}>
                      {referenceRecord.name}
                    </Link>{" "}
                    <RelativeDate date={activity.date} />
                  </span>
                </>
              ) : null
            }
          />
        </div>
      }
      text={companyNote.text}
      link={`/companies/${companyNote.company_id}/show`}
    />
  );
};
